import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Inbox, Send, Clock, CheckCircle, FileText, Plus,
  ArrowLeft, MessageSquare, History, Search, Filter,
  Copy, Archive, Trash2, RefreshCw, User as UserIcon,
  AlertTriangle, ChevronRight, X, Edit, Check, XCircle, ShieldCheck, Workflow,
  LayoutGrid, List as ListIcon
} from 'lucide-react';
import AnfragenKanban from './AnfragenKanban';

const PRIORITY_CONFIG = {
  low: { label: 'Niedrig', color: '#6b7280', badge: 'secondary' },
  normal: { label: 'Normal', color: '#3b82f6', badge: 'default' },
  high: { label: 'Hoch', color: '#ef4444', badge: 'destructive' },
};

const Anfrageportal = () => {
  const { user, hasFeatureAccess } = useContext(AuthContext);
  const [view, setView] = useState('dashboard'); // dashboard, list, detail, create
  const [loading, setLoading] = useState(true);

  // Data
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dashboard, setDashboard] = useState({ assigned_to_me: 0, created_by_me: 0, drafts: 0, awaiting_approval: 0 });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);

  // Filters
  const [activeFilter, setActiveFilter] = useState('all');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [listMode, setListMode] = useState(() => localStorage.getItem('anfragen-list-mode') || 'list'); // 'list' | 'kanban'

  // Create form
  const [createTypeId, setCreateTypeId] = useState('');
  const [formData, setFormData] = useState({});
  const [createPriority, setCreatePriority] = useState('normal');

  // Comment
  const [newComment, setNewComment] = useState('');

  // Approval / Workflow
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve'); // 'approve' | 'reject'
  const [approvalComment, setApprovalComment] = useState('');
  const [showStartStepDialog, setShowStartStepDialog] = useState(false);
  const [selectedStepId, setSelectedStepId] = useState('');

  const canEdit = user?.role === 'admin' ||
    user?.accessible_features?.some(f => f.id === 'anfrageportal' && (f.permission_level === 'edit' || f.permission_level === 'admin'));

  useEffect(() => { loadInitial(); }, []);

  useEffect(() => { localStorage.setItem('anfragen-list-mode', listMode); }, [listMode]);

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [typesRes, dashRes, usersRes] = await Promise.all([
        axios.get('/anfragen/types'),
        axios.get('/anfragen/dashboard'),
        axios.get('/anfragen/users'),
      ]);
      setTypes(typesRes.data.filter(t => t.is_active));
      setDashboard(dashRes.data);
      setUsers(usersRes.data);
    } catch (err) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const loadRequests = async (filter = 'all') => {
    setActiveFilter(filter);
    setView('list');
    try {
      const params = new URLSearchParams();
      if (filter === 'assigned') params.set('assigned_to_me', 'true');
      if (filter === 'created') params.set('created_by_me', 'true');
      if (filter === 'approval') params.set('awaiting_approval', 'true');
      if (filter === 'drafts') { params.set('created_by_me', 'true'); params.set('include_drafts', 'true'); }
      if (filterTypeId) params.set('type_id', filterTypeId);
      if (filterStatus) params.set('status', filterStatus);
      if (searchQuery) params.set('search', searchQuery);
      const res = await axios.get(`/anfragen/requests?${params.toString()}`);
      setRequests(filter === 'drafts' ? res.data.filter(r => r.is_draft) : res.data);
    } catch (err) { toast.error('Fehler beim Laden'); }
  };

  const openDetail = async (req) => {
    setView('detail');
    try {
      const [reqRes, commRes, histRes] = await Promise.all([
        axios.get(`/anfragen/requests/${req.id}`),
        axios.get(`/anfragen/requests/${req.id}/comments`),
        axios.get(`/anfragen/requests/${req.id}/history`),
      ]);
      setSelectedRequest(reqRes.data);
      setComments(commRes.data);
      setHistory(histRes.data);
    } catch (err) { toast.error('Fehler'); }
  };

  const openCreate = (typeId = '') => {
    setCreateTypeId(typeId || (types[0]?.id || ''));
    setFormData({});
    setCreatePriority('normal');
    setView('create');
  };

  const submitRequest = async (isDraft = false) => {
    if (!createTypeId) { toast.error('Bitte Anfrageart wählen'); return; }
    try {
      const res = await axios.post('/anfragen/requests', {
        anfrage_type_id: createTypeId, form_data: formData,
        priority: createPriority, is_draft: isDraft,
      });
      toast.success(isDraft ? 'Entwurf gespeichert' : 'Anfrage eingereicht');
      loadInitial();
      openDetail(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const updateRequest = async (requestId, data) => {
    try {
      const res = await axios.patch(`/anfragen/requests/${requestId}`, data);
      setSelectedRequest(res.data);
      toast.success('Aktualisiert');
      // Refresh history
      const histRes = await axios.get(`/anfragen/requests/${requestId}/history`);
      setHistory(histRes.data);
      loadInitial();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedRequest) return;
    try {
      await axios.post(`/anfragen/requests/${selectedRequest.id}/comments`, { text: newComment.trim() });
      setNewComment('');
      const res = await axios.get(`/anfragen/requests/${selectedRequest.id}/comments`);
      setComments(res.data);
      toast.success('Kommentar hinzugefügt');
    } catch (err) { toast.error('Fehler'); }
  };

  const duplicateRequest = async (reqId) => {
    try {
      const res = await axios.post(`/anfragen/requests/${reqId}/duplicate`);
      toast.success('Anfrage dupliziert (als Entwurf)');
      openDetail(res.data);
    } catch (err) { toast.error('Fehler'); }
  };

  const archiveRequest = async (reqId) => {
    try {
      await axios.post(`/anfragen/requests/${reqId}/archive`);
      toast.success('Archiviert');
      setView('dashboard');
      loadInitial();
    } catch (err) { toast.error('Fehler'); }
  };

  const deleteRequest = async (reqId) => {
    if (!window.confirm('Anfrage löschen?')) return;
    try {
      await axios.delete(`/anfragen/requests/${reqId}`);
      toast.success('Gelöscht');
      setView('dashboard');
      loadInitial();
    } catch (err) { toast.error('Fehler'); }
  };

  const startWorkflowStep = async () => {
    if (!selectedRequest || !selectedStepId) return;
    try {
      const res = await axios.post(`/anfragen/requests/${selectedRequest.id}/start-step`, { step_id: selectedStepId });
      setSelectedRequest(res.data);
      const histRes = await axios.get(`/anfragen/requests/${selectedRequest.id}/history`);
      setHistory(histRes.data);
      toast.success('Freigabe angefordert');
      setShowStartStepDialog(false);
      setSelectedStepId('');
      loadInitial();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const submitApproval = async () => {
    if (!selectedRequest) return;
    try {
      const endpoint = approvalAction === 'approve' ? 'approve' : 'reject';
      const payload = approvalAction === 'approve'
        ? { comment: approvalComment.trim() || null }
        : { reason: approvalComment.trim() };
      if (approvalAction === 'reject' && !approvalComment.trim()) {
        toast.error('Bitte Ablehnungsgrund angeben');
        return;
      }
      const res = await axios.post(`/anfragen/requests/${selectedRequest.id}/${endpoint}`, payload);
      setSelectedRequest(res.data);
      const [commRes, histRes] = await Promise.all([
        axios.get(`/anfragen/requests/${selectedRequest.id}/comments`),
        axios.get(`/anfragen/requests/${selectedRequest.id}/history`),
      ]);
      setComments(commRes.data);
      setHistory(histRes.data);
      toast.success(approvalAction === 'approve' ? 'Freigegeben' : 'Abgelehnt');
      setShowApprovalDialog(false);
      setApprovalComment('');
      loadInitial();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const getUserName = (id) => {
    const u = users.find(u => u.id === id);
    return u?.name || u?.email || id;
  };

  // ----- Conditional Fields evaluator -----
  // Returns true if a field's conditions are met (visible). Empty/missing conditions = always visible.
  const isFieldVisible = (field, data) => {
    const conditions = field.conditions || [];
    if (conditions.length === 0) return true;
    return conditions.every(c => {
      if (!c.field_id) return true;
      const v = (data || {})[c.field_id];
      const target = c.value;
      switch (c.operator) {
        case 'equals':
          if (Array.isArray(v)) return v.includes(target);
          if (typeof v === 'boolean') return Boolean(target) === v;
          return String(v ?? '') === String(target ?? '');
        case 'not_equals':
          if (Array.isArray(v)) return !v.includes(target);
          if (typeof v === 'boolean') return Boolean(target) !== v;
          return String(v ?? '') !== String(target ?? '');
        case 'contains':
          if (Array.isArray(v)) return v.includes(target);
          return String(v ?? '').toLowerCase().includes(String(target ?? '').toLowerCase());
        case 'not_empty':
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === 'boolean') return v === true;
          return v !== undefined && v !== null && String(v).trim() !== '';
        case 'empty':
          if (Array.isArray(v)) return v.length === 0;
          if (typeof v === 'boolean') return v === false;
          return v === undefined || v === null || String(v).trim() === '';
        default:
          return true;
      }
    });
  };

  // Get form fields for selected create type
  const createType = types.find(t => t.id === createTypeId);
  const createFields = createType?.fields || [];

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-[#e30613] mr-2" /><span>Lade...</span></div>;

  // ======= DASHBOARD =======
  if (view === 'dashboard') {
    return (
      <div className="space-y-6" data-testid="anfrageportal-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Anfrageportal</h1>
            <p className="text-sm text-gray-500 mt-1">Interne Anfragen erstellen und verwalten</p>
          </div>
          {canEdit && (
            <Button onClick={() => openCreate()} data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Neue Anfrage
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'assigned', label: 'Mir zugewiesen', value: dashboard.assigned_to_me, icon: Inbox, color: '#3b82f6' },
            { key: 'created', label: 'Von mir erstellt', value: dashboard.created_by_me, icon: Send, color: '#10b981' },
            { key: 'drafts', label: 'Entwürfe', value: dashboard.drafts, icon: FileText, color: '#f59e0b' },
            { key: 'approval', label: 'Wartet auf Freigabe', value: dashboard.awaiting_approval, icon: Clock, color: '#8b5cf6' },
          ].map(s => (
            <Card key={s.key} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => loadRequests(s.key)} data-testid={`stat-${s.key}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                  </div>
                  <s.icon className="w-8 h-8 opacity-20" style={{ color: s.color }} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Types */}
        <Card>
          <CardHeader><CardTitle className="text-base">Anfrage erstellen</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {types.map(t => (
                <div
                  key={t.id}
                  className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-all hover:border-[#e30613]/30"
                  onClick={() => canEdit && openCreate(t.id)}
                  data-testid={`type-card-${t.id}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: t.color + '20' }}>
                    <FileText className="w-5 h-5" style={{ color: t.color }} />
                  </div>
                  <p className="font-medium text-sm">{t.name}</p>
                  {t.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>}
                </div>
              ))}
              {types.length === 0 && <p className="text-sm text-gray-500 col-span-full text-center py-4">Noch keine Anfragearten konfiguriert</p>}
            </div>
          </CardContent>
        </Card>

        {/* All Requests Quick View */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Alle Anfragen</CardTitle>
            <Button variant="outline" size="sm" onClick={() => loadRequests('all')} data-testid="view-all-btn">Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // ======= LIST VIEW =======
  if (view === 'list') {
    return (
      <div className="space-y-4" data-testid="anfrageportal-list">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setView('dashboard'); loadInitial(); }} data-testid="back-to-dashboard-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
            <h2 className="text-lg font-bold">
              {activeFilter === 'assigned' && 'Mir zugewiesen'}
              {activeFilter === 'created' && 'Von mir erstellt'}
              {activeFilter === 'drafts' && 'Entwürfe'}
              {activeFilter === 'approval' && 'Wartet auf Freigabe'}
              {activeFilter === 'all' && 'Alle Anfragen'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input className="pl-8 w-48 h-8 text-sm" placeholder="Suchen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadRequests(activeFilter)} data-testid="search-input" />
            </div>
            <Select value={filterTypeId || 'all'} onValueChange={v => { setFilterTypeId(v === 'all' ? '' : v); }}>
              <SelectTrigger className="h-8 w-40 text-sm"><SelectValue placeholder="Alle Typen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => loadRequests(activeFilter)} data-testid="filter-btn"><Filter className="w-4 h-4" /></Button>
            {/* List/Kanban toggle */}
            <div className="flex items-center rounded-md border bg-white dark:bg-gray-800 overflow-hidden">
              <Button
                size="sm"
                variant={listMode === 'list' ? 'default' : 'ghost'}
                className="h-8 rounded-none px-2"
                onClick={() => setListMode('list')}
                data-testid="view-mode-list-btn"
                title="Listenansicht"
              >
                <ListIcon className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant={listMode === 'kanban' ? 'default' : 'ghost'}
                className="h-8 rounded-none px-2"
                onClick={() => setListMode('kanban')}
                data-testid="view-mode-kanban-btn"
                title="Kanban-Ansicht"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {requests.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-500">Keine Anfragen gefunden</CardContent></Card>
        ) : listMode === 'kanban' ? (
          <AnfragenKanban
            requests={requests}
            types={types}
            onOpen={openDetail}
            onUpdated={() => loadRequests(activeFilter)}
          />
        ) : (
          <div className="space-y-2">
            {requests.map(r => (
              <Card key={r.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetail(r)} data-testid={`request-row-${r.id}`}>
                <CardContent className="py-3 flex items-center gap-4">
                  <div className="w-2 h-10 rounded-full" style={{ backgroundColor: r.status_color || '#6b7280' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm truncate">
                        {Object.values(r.form_data || {})[0] || 'Ohne Titel'}
                      </span>
                      {r.is_draft && <Badge variant="outline" className="text-[10px]">Entwurf</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: r.type_color }}>{r.type_name}</Badge>
                      <span>{r.creator_name}</span>
                      <span>&middot;</span>
                      <span>{r.created_at && format(new Date(r.created_at), 'dd.MM.yy HH:mm', { locale: de })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status_name && <Badge style={{ backgroundColor: r.status_color, color: 'white' }} className="text-[10px]">{r.status_name}</Badge>}
                    <Badge variant={PRIORITY_CONFIG[r.priority]?.badge || 'secondary'} className="text-[10px]">{PRIORITY_CONFIG[r.priority]?.label || r.priority}</Badge>
                    {r.assignee_name && <span className="text-xs text-gray-400">{r.assignee_name}</span>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ======= CREATE VIEW =======
  if (view === 'create') {
    return (
      <div className="space-y-4 max-w-2xl" data-testid="anfrageportal-create">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setView('dashboard')}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
          <h2 className="text-lg font-bold">Neue Anfrage</h2>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label>Anfrageart</Label>
              <Select value={createTypeId} onValueChange={v => { setCreateTypeId(v); setFormData({}); }}>
                <SelectTrigger data-testid="create-type-select"><SelectValue placeholder="Wählen..." /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Dynamic Fields */}
            {createFields.filter(f => isFieldVisible(f, formData)).map(f => (
              <div key={f.id} data-testid={`create-field-${f.id}`}>
                <Label>{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
                {f.help_text && <p className="text-xs text-gray-400 mb-1">{f.help_text}</p>}

                {f.type === 'short_text' && (
                  <Input value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} placeholder={f.placeholder} readOnly={f.readonly} />
                )}
                {f.type === 'long_text' && (
                  <Textarea value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} placeholder={f.placeholder} readOnly={f.readonly} />
                )}
                {f.type === 'number' && (
                  <Input type="number" value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} placeholder={f.placeholder} />
                )}
                {(f.type === 'date' || f.type === 'datetime') && (
                  <Input type={f.type === 'datetime' ? 'datetime-local' : 'date'} value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} />
                )}
                {f.type === 'dropdown' && (
                  <Select value={formData[f.id] || '_none'} onValueChange={v => setFormData(p => ({ ...p, [f.id]: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder={f.placeholder || 'Auswählen...'} /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">-</SelectItem>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {f.type === 'checkbox' && (
                  <div className="space-y-1">{(f.options || []).map(o => (
                    <label key={o} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={(formData[f.id] || []).includes(o)} onChange={e => {
                        const current = formData[f.id] || [];
                        setFormData(p => ({ ...p, [f.id]: e.target.checked ? [...current, o] : current.filter(x => x !== o) }));
                      }} className="rounded" />
                      {o}
                    </label>
                  ))}</div>
                )}
                {f.type === 'yes_no' && (
                  <div className="flex items-center gap-2"><Switch checked={formData[f.id] || false} onCheckedChange={v => setFormData(p => ({ ...p, [f.id]: v }))} /><span className="text-sm">{formData[f.id] ? 'Ja' : 'Nein'}</span></div>
                )}
                {f.type === 'url' && (
                  <Input type="url" value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} placeholder="https://..." />
                )}
                {f.type === 'user_select' && (
                  <Select value={formData[f.id] || '_none'} onValueChange={v => setFormData(p => ({ ...p, [f.id]: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="User wählen..." /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">-</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {['file_upload', 'image_upload', 'pdf_upload'].includes(f.type) && (
                  <div className="text-xs text-gray-400 italic p-3 border rounded bg-gray-50 dark:bg-gray-800">
                    Datei-Upload wird nach dem Speichern verfügbar
                  </div>
                )}
              </div>
            ))}

            <div>
              <Label>Priorität</Label>
              <Select value={createPriority} onValueChange={setCreatePriority}>
                <SelectTrigger data-testid="create-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => submitRequest(true)} data-testid="save-draft-btn"><FileText className="w-4 h-4 mr-1" /> Entwurf</Button>
              <Button onClick={() => submitRequest(false)} className="bg-[#e30613] hover:bg-[#c00510]" data-testid="submit-request-btn"><Send className="w-4 h-4 mr-1" /> Einreichen</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ======= DETAIL VIEW =======
  if (view === 'detail' && selectedRequest) {
    const req = selectedRequest;
    const typeInfo = req.type_info || {};
    const formFields = typeInfo.fields || req.form_snapshot || [];
    const statuses = typeInfo.statuses || [];
    const workflowSteps = typeInfo.workflow_steps || [];
    const isPendingApprover = (req.pending_approvers || []).includes(user.id);
    const hasPendingApproval = (req.pending_approvers || []).length > 0;

    return (
      <div className="space-y-4" data-testid="anfrageportal-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setView('list'); loadRequests(activeFilter); }}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
            <Badge style={{ backgroundColor: req.type_color, color: 'white' }}>{req.type_name}</Badge>
            {req.is_draft && <Badge variant="outline">Entwurf</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => duplicateRequest(req.id)}><Copy className="w-4 h-4 mr-1" /> Duplizieren</Button>
            {!req.is_archived && <Button size="sm" variant="outline" onClick={() => archiveRequest(req.id)}><Archive className="w-4 h-4 mr-1" /> Archivieren</Button>}
            {(req.creator_id === user.id || user.role === 'admin') && <Button size="sm" variant="outline" onClick={() => deleteRequest(req.id)}><Trash2 className="w-4 h-4 mr-1 text-red-500" /></Button>}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left: Form Data */}
          <div className="col-span-12 lg:col-span-8 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Anfrage-Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {formFields.filter(f => isFieldVisible(f, req.form_data || {})).map(f => {
                  const val = (req.form_data || {})[f.id];
                  if (val === undefined || val === null || val === '') return null;
                  return (
                    <div key={f.id} data-testid={`detail-field-${f.id}`}>
                      <Label className="text-xs text-gray-500">{f.label}</Label>
                      <p className="text-sm mt-0.5">
                        {Array.isArray(val) ? val.join(', ') : typeof val === 'boolean' ? (val ? 'Ja' : 'Nein') : String(val)}
                      </p>
                    </div>
                  );
                })}
                {formFields.length === 0 && Object.entries(req.form_data || {}).map(([k, v]) => (
                  <div key={k}><Label className="text-xs text-gray-500">{k}</Label><p className="text-sm">{String(v)}</p></div>
                ))}
              </CardContent>
            </Card>

            {/* Comments */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Kommentare ({comments.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {comments.map(c => (
                    <div key={c.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold">{c.user_name}</span>
                        <span className="text-[10px] text-gray-400">{format(new Date(c.created_at), 'dd.MM.yy HH:mm', { locale: de })}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{c.text}</p>
                    </div>
                  ))}
                  {comments.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Noch keine Kommentare</p>}
                </div>
                {canEdit && !req.is_archived && (
                  <div className="flex gap-2">
                    <Input placeholder="Kommentar..." value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && addComment()} data-testid="comment-input" />
                    <Button size="sm" onClick={addComment} disabled={!newComment.trim()} data-testid="send-comment-btn"><Send className="w-4 h-4" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* History */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Verlauf</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {history.map(h => (
                    <div key={h.id} className="flex items-start gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">{h.details}</span>
                        <span className="text-xs text-gray-400 ml-2">{h.user_name} &middot; {format(new Date(h.timestamp), 'dd.MM.yy HH:mm', { locale: de })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Sidebar */}
          <div className="col-span-12 lg:col-span-4 space-y-4">
            {/* Approval Banner */}
            {isPendingApprover && !req.is_archived && (
              <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20" data-testid="approval-banner">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-5 h-5 text-orange-600" />
                    <span className="font-semibold text-sm">Freigabe erforderlich</span>
                  </div>
                  {req.current_step_name && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">Schritt: {req.current_step_name}</p>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => { setApprovalAction('approve'); setApprovalComment(''); setShowApprovalDialog(true); }} data-testid="approve-btn">
                      <Check className="w-4 h-4 mr-1" /> Freigeben
                    </Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => { setApprovalAction('reject'); setApprovalComment(''); setShowApprovalDialog(true); }} data-testid="reject-btn">
                      <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pending status indicator (for non-approvers) */}
            {hasPendingApproval && !isPendingApprover && !req.is_archived && (
              <Card className="border-blue-300 bg-blue-50 dark:bg-blue-950/20">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">Wartet auf Freigabe</span>
                  </div>
                  {req.current_step_name && (
                    <p className="text-xs text-gray-500 mt-1">Schritt: {req.current_step_name}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Workflow - Start Step */}
            {canEdit && !req.is_archived && !req.is_draft && workflowSteps.length > 0 && !hasPendingApproval && (
              <Card>
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Workflow className="w-4 h-4" /> Workflow</CardTitle></CardHeader>
                <CardContent>
                  <Button size="sm" variant="outline" className="w-full" onClick={() => { setSelectedStepId(workflowSteps[0]?.id || ''); setShowStartStepDialog(true); }} data-testid="start-step-btn">
                    <Send className="w-4 h-4 mr-1" /> Freigabe anfordern
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Status */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Status</CardTitle></CardHeader>
              <CardContent>
                {req.status_name && (
                  <Badge className="mb-3 text-sm" style={{ backgroundColor: req.status_color, color: 'white' }}>{req.status_name}</Badge>
                )}
                {canEdit && !req.is_archived && !req.is_draft && (
                  <Select value={req.status_id || ''} onValueChange={v => updateRequest(req.id, { status_id: v })}>
                    <SelectTrigger className="text-sm" data-testid="status-select"><SelectValue placeholder="Status wählen..." /></SelectTrigger>
                    <SelectContent>{statuses.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</div>
                      </SelectItem>
                    ))}</SelectContent>
                  </Select>
                )}
                {req.is_draft && canEdit && (
                  <Button size="sm" className="w-full mt-2 bg-[#e30613] hover:bg-[#c00510]" onClick={() => updateRequest(req.id, { is_draft: false })} data-testid="submit-draft-btn">
                    <Send className="w-4 h-4 mr-1" /> Einreichen
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Assignee */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Bearbeiter</CardTitle></CardHeader>
              <CardContent>
                {req.assignee_name && <p className="text-sm mb-2">{req.assignee_name}</p>}
                {canEdit && !req.is_archived && (
                  <Select value={req.assignee_id || '_none'} onValueChange={v => updateRequest(req.id, { assignee_id: v === '_none' ? '' : v })}>
                    <SelectTrigger className="text-sm" data-testid="assignee-select"><SelectValue placeholder="Zuweisen..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Nicht zugewiesen</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>

            {/* Priority */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Priorität</CardTitle></CardHeader>
              <CardContent>
                {canEdit && !req.is_archived ? (
                  <Select value={req.priority || 'normal'} onValueChange={v => updateRequest(req.id, { priority: v })}>
                    <SelectTrigger className="text-sm" data-testid="priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Badge variant={PRIORITY_CONFIG[req.priority]?.badge}>{PRIORITY_CONFIG[req.priority]?.label}</Badge>
                )}
              </CardContent>
            </Card>

            {/* Info */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Info</CardTitle></CardHeader>
              <CardContent className="text-xs text-gray-500 space-y-1">
                <p>Erstellt: {req.created_at && format(new Date(req.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
                <p>Ersteller: {req.creator_name}</p>
                {req.submitted_at && <p>Eingereicht: {format(new Date(req.submitted_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>}
                {req.updated_at && <p>Aktualisiert: {format(new Date(req.updated_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Approval Dialog */}
        <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
          <DialogContent data-testid="approval-dialog">
            <DialogHeader>
              <DialogTitle>{approvalAction === 'approve' ? 'Freigeben' : 'Ablehnen'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label>{approvalAction === 'approve' ? 'Kommentar (optional)' : 'Ablehnungsgrund'} {approvalAction === 'reject' && <span className="text-red-500">*</span>}</Label>
              <Textarea value={approvalComment} onChange={e => setApprovalComment(e.target.value)} placeholder={approvalAction === 'approve' ? 'Optional...' : 'Bitte begründen Sie die Ablehnung'} data-testid="approval-comment-input" rows={3} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Abbrechen</Button>
                <Button
                  onClick={submitApproval}
                  className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  variant={approvalAction === 'reject' ? 'destructive' : 'default'}
                  data-testid="submit-approval-btn"
                >
                  {approvalAction === 'approve' ? <><Check className="w-4 h-4 mr-1" /> Freigeben</> : <><XCircle className="w-4 h-4 mr-1" /> Ablehnen</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Start Step Dialog */}
        <Dialog open={showStartStepDialog} onOpenChange={setShowStartStepDialog}>
          <DialogContent data-testid="start-step-dialog">
            <DialogHeader>
              <DialogTitle>Freigabe anfordern</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Workflow-Schritt</Label>
              <Select value={selectedStepId} onValueChange={setSelectedStepId}>
                <SelectTrigger data-testid="step-select"><SelectValue placeholder="Schritt wählen..." /></SelectTrigger>
                <SelectContent>
                  {workflowSteps.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({(s.approver_ids || []).length} Genehmiger)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowStartStepDialog(false)}>Abbrechen</Button>
                <Button onClick={startWorkflowStep} disabled={!selectedStepId} data-testid="confirm-start-step-btn">
                  <Send className="w-4 h-4 mr-1" /> Anfordern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return null;
};

export default Anfrageportal;
